import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  InputLabel,
  OutlinedInput,
  LinearProgress,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import Layout from "../../components/layout";
import ValidationModal from "../BootstrapValidationModal";
import salonService from "../../services/salonServices";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DecryptData, EncryptData, jsonParser } from "../../helpers/helper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

function EditDeal() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>(); // deal ID from URL

  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingModalOpen, setIsSubmittingModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [serviceName, setServiceName] = useState("");
  const [serviceNameError, setServiceNameError] = useState(false);

  const [price, setPrice] = useState<string>("");
  const [priceError, setPriceError] = useState(false);

  const [description, setDescription] = useState("");

  const [serviceInput, setServiceInput] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [servicesError, setServicesError] = useState(false);

  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);

  const [serviceTypeData, setServiceTypeData] = useState<any[]>([]);
  const [userId, setUserId] = useState<number>();

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    const fetchServiceType = async () => {
      console.log(EncryptData('Protein Treatment'))
      console.log(EncryptData('Hair Cut'))
      console.log(EncryptData('Blow Dry'))

      const data = await salonService.findAll();
      const decryptData = await DecryptData(data.serviceType);
      const parsedData = jsonParser(decryptData);

      setServiceTypeData(
        parsedData.map((d) => ({
          ...d,
          serviceName: DecryptData(d.serviceName),
        }))
      );

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      setUserId(user.id);
    };

    const fetchDeal = async () => {
      try {
        const response = await salonService.findOne(id);
        const deal = jsonParser(await DecryptData(response.serviceData));

        setServiceName(DecryptData(deal.serviceName));
        setPrice(DecryptData(deal.finalMaxPrice));
        setDescription(DecryptData(deal.description));
        setServices(deal.relatedService.map((s: string) => DecryptData(s)));
        setStartDate(dayjs(deal.startDate));
        setEndDate(dayjs(deal.endDate));
      } catch (err) {
        toast.error("Failed to fetch deal data");
        history.push("/services");
      }
    };

    fetchServiceType();
    fetchDeal();
  }, [id, history]);

  /* ---------------- PROGRESS MODAL ---------------- */
  useEffect(() => {
    if (!isSubmittingModalOpen) return;

    let value = 0;
    const interval = setInterval(() => {
      value += 5;
      setProgress(value);
      if (value >= 100) {
        clearInterval(interval);
        history.push("/services");
      }
    }, 30);

    return () => clearInterval(interval);
  }, [isSubmittingModalOpen, history]);

  /* ---------------- HANDLERS ---------------- */
  const handleAddService = () => {
    if (!serviceInput.trim()) return;
    if (!services.includes(serviceInput.trim())) {
      setServices([...services, serviceInput.trim()]);
    }
    setServiceInput("");
    setServicesError(false);
  };

  const handleServiceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddService();
    }
  };

  const handleRemoveService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    let error = 0;

    if (!serviceName) {
      setServiceNameError(true);
      error++;
    }

    if (!price) {
      setPriceError(true);
      error++;
    }

    if (!startDate) {
      setStartDateError(true);
      error++;
    }

    if (!endDate || endDate <= startDate) {
      setEndDateError(true);
      toast.error("End date must be greater than start date");
      error++;
    }

    if (services.length === 0) {
      setServicesError(true);
      error++;
    }

    if (error > 0) {
      setIsModalOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const deal = serviceTypeData.find((s) => s.serviceName === "Deals");

      const payload = {
        serviceName: EncryptData(serviceName),
        price: EncryptData(price),
        description: EncryptData(description),
        serviceTypeId: deal?.id,
        startDate,
        endDate,
        userId,
        services: services.map((s) => EncryptData(s)),
        deal: true,
      };

      await salonService.update(id, payload); // <-- update instead of save
      setIsSubmittingModalOpen(true);
    } catch (err) {
      toast.error("Something went wrong");
      setIsSubmitting(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Layout>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            background: "#fff",
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
            p: { xs: 2, sm: 4 },
          }}
        >
          <h3 style={{ marginBottom: 24 }}>Edit Deal</h3>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InputLabel shrink>Deal Name</InputLabel>
              <OutlinedInput
                fullWidth
                value={serviceName}
                error={serviceNameError}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="Enter deal name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel shrink>Price</InputLabel>
              <OutlinedInput
                fullWidth
                value={price}
                error={priceError}
                onChange={(e) =>
                  setPrice(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="Enter price"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel shrink>Start Date</InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel shrink>End Date</InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <InputLabel shrink>Services (Manual Entry)</InputLabel>
              <OutlinedInput
                fullWidth
                value={serviceInput}
                error={servicesError}
                onChange={(e) => setServiceInput(e.target.value)}
                onKeyDown={handleServiceKeyDown}
                placeholder="Type service and press Enter"
                endAdornment={
                  <Button onClick={handleAddService} size="small">
                    Add
                  </Button>
                }
              />

              <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                {services.map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    color="success"
                    onDelete={() => handleRemoveService(i)}
                  />
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <InputLabel shrink>Description</InputLabel>
              <OutlinedInput
                fullWidth
                multiline
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description..."
              />
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                size="large"
                disabled={isSubmitting}
                onClick={handleSubmit}
                sx={{
                  px: 5,
                  borderRadius: 2,
                  background: "#719879",
                  "&:hover": { background: "#5f8267" },
                }}
              >
                {isSubmitting ? "Submitting..." : "Update Deal"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* SUCCESS MODAL */}
      <ValidationModal
        isOpen={isSubmittingModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <Box p={3}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5 }}
          />
          <p style={{ marginTop: 10 }}>Submitting Request...</p>
        </Box>
      </ValidationModal>

      {/* ERROR MODAL */}
      <ValidationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <Box p={3}>
          <p>Please fill all required fields</p>
          <Button onClick={() => setIsModalOpen(false)}>OK</Button>
        </Box>
      </ValidationModal>
    </Layout>
  );
}

export default EditDeal;
